// DominusMark.tsx — DOMINUS logo mark for the admin console.
//
// Admin uses mark-only configuration (no wordmark inside the SVG) per Brand Bible Ch 6:
// "App icon, favicon, loading badge, round win overlay, nav indicator."
// The wordmark "DOMINUS" is rendered as separate Cinzel Decorative text alongside it.
//
// SVG coordinates are CANONICAL — never alter. (Brand Bible Ch 9, DESIGN.md §7)
//   D path:  M38 22 L38 178 L92 178 C138 178 164 152 164 100 C164 48 138 22 92 22 Z
//   Tile:    x=57 y=40 w=38 h=120 rx=5
//   [1] ace: cx=76 cy=70 r=5.5 #FFE066
//   [6]: cols x=67/x=85, rows y=112/127/142, r=3.8 #C9A84C

import React from 'react';

interface DominusMarkProps {
  size?: number;
  className?: string;
}

export default function DominusMark({ size = 32, className = '' }: DominusMarkProps): React.ReactElement {
  // Gradient ID uniqueness — prevents collisions if multiple marks are on the same page.
  const gradId = `dm-grad-${size}`;

  return (
    <svg
      className={`dm-svg ${className}`}
      viewBox="0 0 200 200"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="DOMINUS mark"
      role="img"
    >
      <defs>
        <linearGradient id={gradId} x1="20" y1="20" x2="160" y2="160" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="var(--gold-d)" />
          <stop offset="30%"  stopColor="var(--gold)" />
          <stop offset="52%"  stopColor="var(--gold-b)" />
          <stop offset="72%"  stopColor="var(--gold)" />
          <stop offset="100%" stopColor="var(--gold-d)" />
        </linearGradient>
      </defs>

      {/* D letterform — canonical path, never alter */}
      <path
        d="M38 22 L38 178 L92 178 C138 178 164 152 164 100 C164 48 138 22 92 22 Z"
        stroke={`url(#${gradId})`}
        strokeWidth="3.5"
        fill="rgba(201,168,76,0.02)"
      />

      {/* Tile rectangle inside the D counter-space */}
      <rect
        x="57" y="40" width="38" height="120" rx="5"
        fill="rgba(201,168,76,0.07)"
        stroke="rgba(201,168,76,0.55)"
        strokeWidth="1.3"
      />

      {/* Divider line (tile centre) */}
      <line x1="61" y1="100" x2="91" y2="100" stroke="rgba(201,168,76,0.55)" strokeWidth="0.9" />

      {/* [1] pip — top half. #FFE066 = --gold-b (Brand Bible Ch 4: one sun) */}
      <circle cx="76" cy="70" r="5.5" fill="var(--gold-b)" />

      {/* [6] pips — bottom half. #C9A84C = --gold (six stars) */}
      <circle cx="67" cy="112" r="3.8" fill="var(--gold)" />
      <circle cx="85" cy="112" r="3.8" fill="var(--gold)" />
      <circle cx="67" cy="127" r="3.8" fill="var(--gold)" />
      <circle cx="85" cy="127" r="3.8" fill="var(--gold)" />
      <circle cx="67" cy="142" r="3.8" fill="var(--gold)" />
      <circle cx="85" cy="142" r="3.8" fill="var(--gold)" />
    </svg>
  );
}
